import api from "../api";
import createAppAsyncThunk from "../createAppAsyncThunk";
import {
  ADD_BLOG,
  GET_BLOG,
  UPDATE_BLOG,
  ADD_POST,
  GET_POSTS,
  DELETE_POSTS,
  CHANGE_STATUS,
  GET_POST_BY_ID,
  UPDATE_POST,
  DELETE_BLOG,
  CLEAR_MESSAGE,
} from "./blogTypes";
import { GET_ERRORS } from "../errorServices/errorTypes";
import { clearUrls } from "../ImageServices/ImageActions";
import { hasDisplayableError } from "../../utils/getErrorMessage";
import { asCaught, errorPayload } from "../caughtError";
import type { AxiosResponse } from "axios";
import type {
  BlogsResponse,
  PostsResponse,
  MessageResponse,
} from "../../../../shared/apiTypes";

export const clearMessage = () => (dispatch) => {
  dispatch({
    type: CLEAR_MESSAGE,
  });
};

/**********************  BLOG **********************/

// Add Blog
export const addBlog = (data) => async (dispatch) => {
  try {
    const res: AxiosResponse<MessageResponse> = await api.post("/api/blog/add", data);
    dispatch({ type: ADD_BLOG, payload: res.data });
    await dispatch(getBlog());
  } catch (err) {
    // SCRUM-1263: the server body only when the hub would actually show it —
    // otherwise the documented `{ message }` envelope (createAppAsyncThunk :18-22),
    // so a transport failure (no `response`) still carries err.message instead of a
    // literal the reader cannot diagnose.
    const body = asCaught(err).response?.data;
    dispatch({
      type: GET_ERRORS,
      payload: hasDisplayableError(body)
        ? body
        : { message: asCaught(err).message || "Request failed" },
    });
  }
};

//Get Blog
export const getBlog = () => async (dispatch) => {
  let res: AxiosResponse<BlogsResponse>;
  // Keep the GET_BLOG dispatch out of the try: reducers run synchronously
  // inside dispatch(), so a reducer/render error would land in the catch below
  // and be mislabelled as a network failure — and swallowed (SCRUM-948).
  try {
    res = await api.get("/api/blog");
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: asCaught(err).response?.data || "Failed to fetch blog",
    });
    return;
  }
  dispatch({
    type: GET_BLOG,
    payload: res.data,
  });
};

//update Blog
export const updateBlog = (data) => (dispatch) => {
  api
    .put(
      `/api/blog/update/${data._id}`,
      data
    )
    .then((res) => {
      dispatch({
        type: UPDATE_BLOG,
        payload: res.data,
      });
    })
    .then(() => {
      dispatch(getBlog());
    })
    .catch((err) => {
      // SCRUM-1290: an unguarded `err.response.data` threw a TypeError inside
      // this catch on any transport-level failure, so nothing was dispatched
      // and the editor's save appeared to do nothing at all.
      dispatch({
        type: GET_ERRORS,
        payload: err.response?.data ?? { message: err.message },
      });
    });
};

// Delete blog — createAppAsyncThunk (SCRUM-1021 follow-up, RTK consistency). Was a
// legacy re-throwing thunk that also dispatched GET_ERRORS itself; now a failure
// rejects and is surfaced once centrally by appErrorMiddleware (no more hand-rolled
// error dispatch), and the caller awaits .unwrap() to gate its success.
export const deleteBlog = createAppAsyncThunk(
  "blog/deleteBlog",
  async (id: string, { dispatch }) => {
    await api.delete<unknown>(`/api/blog/delete/${id}`);
    dispatch({ type: DELETE_BLOG });
    dispatch(getBlog());
    return id;
  }
);

/**********************  POST **********************/

//add post
export const addPost = (data) => async (dispatch) => {
  try {
    const res: AxiosResponse<MessageResponse> = await api.post("/api/post/add", data);
    dispatch({ type: ADD_POST, payload: res.data });
    dispatch(clearUrls());
    await dispatch(getPosts());
  } catch (err) {
    // SCRUM-1290: `?.` alone leaves `undefined` here on a transport failure, and
    // hasDisplayableError reads that as "cleared" — so the failure raised no
    // toast at all. Same envelope as getPostById below.
    dispatch({
      type: GET_ERRORS,
      payload: asCaught(err).response?.data ?? { message: asCaught(err).message },
    });
  }
};
//get Post by  Id
export const getPostById = (id) => (dispatch) => {
  return api
    .get(`/api/post/${id}`)
    .then((res) => {
      dispatch({
        type: GET_POST_BY_ID,
        payload: res.data,
      });
    })
    .then(() => dispatch({ type: GET_ERRORS, payload: {} }))
    .catch((err) => {
      // Without this the edit form stays blank and the server's reason is lost
      // (SCRUM-1248). `?? { message }` matters for network-level failures,
      // where there is no `err.response` and an undefined payload would read
      // as "cleared" to the errors slice.
      dispatch({
        type: GET_ERRORS,
        payload: err.response?.data ?? { message: err.message },
      });
    });
};
//update post
export const updatePost = (data) => async (dispatch) => {
  let res: AxiosResponse<unknown>;
  try {
    res = await api.put("/api/post/update", data);
  } catch (err) {
    // Report once for the notification hub and re-throw (SCRUM-2374, the
    // updatePostStatus shape of SCRUM-815), so the edit screen learns the save
    // failed from the promise instead of watching state.errors.
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    });
    throw err;
  }
  dispatch({
    type: UPDATE_POST,
    payload: res.data,
  });
  // Re-read outside the awaited chain: a failed re-read reports its own error
  // and must not reject the save that already succeeded.
  dispatch(getPostById(data._id));
};

//get posts
export const getPosts = () => async (dispatch) => {
  try {
    const res: AxiosResponse<PostsResponse> = await api.get("/api/post");
    dispatch({ type: GET_POSTS, payload: res.data });
  } catch (err) {
    dispatch({ type: GET_ERRORS, payload: asCaught(err).response?.data || "Failed to fetch posts" });
  }
};

// Delete post — createAppAsyncThunk (SCRUM-1023 / SCRUM-957 class). A failed
// DELETE now rejects and is surfaced once centrally by appErrorMiddleware
// (replacing the hand-rolled GET_ERRORS catch), and `.unwrap()` lets the caller
// gate its success toast instead of firing it unconditionally.
export const deletePost = createAppAsyncThunk(
  "blog/deletePost",
  async (idPost: string, { dispatch }) => {
    await api.delete<unknown>(`/api/post/delete/${idPost}`);
    dispatch({ type: DELETE_POSTS, payload: idPost });
    return idPost;
  }
);

//change status
export const changeStatus = (data) => (dispatch) => {
  dispatch({
    type: CHANGE_STATUS,
    payload: data,
  });
};

//update Blog posts
export const updatePostStatus = (data) => async (dispatch) => {
  try {
    const res: AxiosResponse<MessageResponse> = await api.put(
      `/api/post/updatepoststatus/${data._id}`,
      data
    );
    // Surface success + re-throw on failure (SCRUM-815) so the caller can revert the
    // optimistic show/hide toggle instead of leaving the UI out of sync with the server.
    return res.data;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    });
    throw err;
  }
};
